/* The finished material, by the carrier it arrives on.

   Three carriers and no more: the material itself as blocks, an application as
   a file, a recording with the script it was read from. Print is not among
   them — a printable sheet is derived from the blocks, which is what `print`
   on the kit item says.

   Each carrier is a component of its own and this is the switch between them.
   The one thing worth saying here rather than in three places: what is content
   and what is not. Blocks and the script of a recording are content, in git; an
   application is a file beside the material; the recording itself is the only
   part of a lesson that comes out of the database.

   **Every carrier is shown as the thing it will be.** A teacher decides whether
   to make a material by looking at it, and a material shown as a slab of the
   portal is a material nobody can picture in their own classroom. So an
   application runs on a screen — see DeviceKind in lib/types.ts — and a
   material that is going to be printed is shown on a sheet of paper. Neither
   is decoration: the first says this is a file you open at the projector, the
   second says this is what comes out of the copier, and where the material is
   cut up the sheet says that too.

   OWNED BY ITEM 3 OF PLAN.md. */

import type { ReactNode } from "react";

import { AppFrame } from "../../blocks/AppFrame";
import { Audio } from "../../blocks/Audio";
import { Blocks } from "../../blocks";
import { useI18n } from "../../lib/i18n";
import type { Ready as ReadyType } from "../../lib/types";

export function Ready({ ready, print }: { ready: ReadyType; print?: boolean }) {
  if (ready.as === "blocks") {
    const blocks = <Blocks blocks={ready.blocks} />;
    return print ? <Paper>{blocks}</Paper> : blocks;
  }

  if (ready.as === "audio") {
    return <Audio audio={ready.audio} script={ready.script} minutes={ready.minutes} />;
  }

  return <AppFrame app={ready.app} height={ready.height} device={ready.device} />;
}

/* The sheet a printed material is shown on.

   White against the portal's warm paper, with the margin a copier leaves and a
   shadow that lifts it off the page — the same trick the QR overlay plays with
   its white plane, and for the same reason: the eye reads the surface before it
   reads a word on it.

   It is `noprint` as a frame and not as content. On paper the sheet *is* the
   page, and a drawing of a sheet printed onto a sheet is the one thing that
   would make the mock ridiculous, so print.css flattens it and keeps what is
   inside. */
function Paper({ children }: { children: ReactNode }) {
  const { ui } = useI18n();
  return (
    <div className="paper">
      <div className="paper-sheet">{children}</div>
      <div className="paper-bar noprint">{ui("paper.a4")}</div>
    </div>
  );
}
